/**
 * User Management Helpers
 * Reusable functions for checking user roles and plans
 */

/**
 * Plan priority order (for comparison)
 * free < pro < growth
 */
const PLAN_PRIORITY = {
  free: 0,
  pro: 1,
  growth: 2,
};

/**
 * Check if a user is an admin
 * @param {Object} user - User document from Firestore
 * @param {string} user.role - User role ('user' | 'admin')
 * @returns {boolean} True if user is admin
 */
export function isAdmin(user) {
  if (!user) return false;
  return user.role === 'admin';
}

/**
 * Check if a user has a specific plan or higher
 * @param {Object} user - User document from Firestore
 * @param {string} user.plan - User's current plan
 * @param {string} requiredPlan - Required plan ('free' | 'pro' | 'growth')
 * @returns {boolean} True if user has required plan or higher
 */
export function hasPlan(user, requiredPlan) {
  if (!user) return false;
  
  const userPlan = user.plan || 'free';
  const userPriority = PLAN_PRIORITY[userPlan] ?? 0;
  const requiredPriority = PLAN_PRIORITY[requiredPlan] ?? 0;
  
  return userPriority >= requiredPriority;
}

/**
 * Check if a user has exactly a specific plan
 * @param {Object} user - User document from Firestore
 * @param {string} user.plan - User's current plan
 * @param {string} plan - Plan to check ('free' | 'pro' | 'growth')
 * @returns {boolean} True if user has exactly this plan
 */
export function hasExactPlan(user, plan) {
  if (!user) return false;
  return (user.plan || 'free') === plan;
}

/**
 * Compare two plans
 * @param {string} plan1 - First plan
 * @param {string} plan2 - Second plan
 * @returns {number} -1 if plan1 < plan2, 0 if equal, 1 if plan1 > plan2
 */
export function comparePlans(plan1, plan2) {
  const priority1 = PLAN_PRIORITY[plan1] ?? 0;
  const priority2 = PLAN_PRIORITY[plan2] ?? 0;
  
  if (priority1 < priority2) return -1;
  if (priority1 > priority2) return 1;
  return 0;
}

/**
 * Get plan priority number
 * @param {string} plan - Plan name
 * @returns {number} Priority number (0 = free, 1 = pro, 2 = growth)
 */
export function getPlanPriority(plan) {
  return PLAN_PRIORITY[plan] ?? 0;
}

/**
 * Check if user can access a feature based on plan
 * @param {Object} user - User document
 * @param {string} featurePlan - Minimum plan required for feature
 * @returns {boolean} True if user can access feature
 */
export function canAccessFeature(user, featurePlan) {
  // Admins always have access
  if (isAdmin(user)) return true;
  
  return hasPlan(user, featurePlan);
}

/**
 * Get user's effective plan (admin always gets growth)
 * @param {Object} user - User document
 * @returns {string} Effective plan
 */
export function getEffectivePlan(user) {
  if (!user) return 'free';
  
  // Admins always have growth plan access
  if (isAdmin(user)) return 'growth';
  
  return user.plan || 'free';
}











